import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Package, Plus, Trash2, Upload, Search,
  CheckCircle, XCircle, ChevronDown, ChevronRight, ChevronLeft,
  History, AlertTriangle, Info, RefreshCw, ExternalLink, Download, Pencil, Check, Tag, Copy, X
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ActionBadge({ action }) {
  const cfg = {
    added:        { color: 'badge-green',  label: 'Added' },
    removed:      { color: 'badge-red',    label: 'Removed' },
    bulk_added:   { color: 'badge-cyan',   label: 'Bulk Added' },
    bulk_removed: { color: 'badge-amber',  label: 'Bulk Removed' },
    list_created:    { color: 'badge-green',  label: 'List Created' },
    list_deleted:    { color: 'badge-red',    label: 'List Deleted' },
    purpose_changed: { color: 'badge-purple', label: 'Purpose Changed' },
    env_changed:     { color: 'badge-cyan',   label: 'Environment Changed' },
  };
  const { color, label } = cfg[action] || { color: 'badge-muted', label: action };
  return <span className={`badge ${color}`}>{label}</span>;
}

// ─── Platform cell with store hyperlink ───────────────────────────────────────
function PlatformCell({ platform, bundleId, listType }) {
  // Mobile platforms with store links
  const storeUrl = platform === 'Google Play'
    ? `https://play.google.com/store/apps/details?id=${bundleId}`
    : platform === 'Apple App Store'
    ? `https://apps.apple.com/app/id${bundleId}`
    : null;

  // CTV store links
  const ctvUrl = platform === 'Roku'       ? `https://channelstore.roku.com/details/${bundleId}`
    : platform === 'Fire TV'  ? `https://www.amazon.com/dp/${bundleId}`
    : platform === 'Apple TV' ? `https://apps.apple.com/app/id${bundleId}`
    : platform === 'Android TV' ? `https://play.google.com/store/apps/details?id=${bundleId}`
    : null;

  const finalUrl = storeUrl || ctvUrl;

  // Color per platform
  const platformColor = {
    'Google Play': 'var(--green)', 'Android TV': 'var(--green)',
    'Apple App Store': 'var(--cyan)', 'Apple TV': 'var(--cyan)',
    'Roku': 'var(--purple)', 'Fire TV': 'var(--amber)',
    'Samsung': 'var(--text-secondary)', 'Web / Other': 'var(--text-muted)',
    'CTV': 'var(--text-muted)',
  };
  const color = platformColor[platform] || 'var(--text-muted)';
  const bgAlpha = finalUrl ? '0.08' : '0.04';
  const bdAlpha = finalUrl ? '0.2' : '0.12';

  const displayLabel = {
    'Apple App Store': 'iOS',
    'Google Play':     'Android',
    'Apple TV':        'tvOS',
    'Android TV':      'Android TV',
    'Fire TV':         'Fire TV',
    'Roku':            'Roku',
    'Samsung':         'Samsung',
    'Web / Other':     'Web',
  }[platform] || platform || '—';

  const style = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    color, fontSize: '11px', fontFamily: 'var(--font-mono)',
    textDecoration: 'none',
    background: `${color.replace('var(--', 'rgba(').replace(')', ', ' + bgAlpha + ')')}`,
    border: `1px solid ${color.replace('var(--', 'rgba(').replace(')', ', ' + bdAlpha + ')')}`,
    padding: '2px 8px', borderRadius: '3px',
  };

  if (!finalUrl) return <span style={style} title={platform}>{displayLabel}</span>;

  return (
    <a href={finalUrl} target="_blank" rel="noopener noreferrer" title={platform}
      style={{ ...style, transition: 'opacity 0.15s', cursor: 'pointer' }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      onClick={e => e.stopPropagation()}>
      {displayLabel} <ExternalLink size={9} />
    </a>
  );
}

// ─── Store status cell ────────────────────────────────────────────────────────
function StoreStatusCell({ status }) {
  if (!status) return <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>;
  if (status === 'checking') return <span className="spinner" style={{ width: '12px', height: '12px' }} />;
  if (status === 'live') return <span className="badge badge-green"><CheckCircle size={10} /> Live</span>;
  if (status === 'not_found') return <span className="badge badge-red"><XCircle size={10} /> Not Found</span>;
  if (status === 'error') return <span className="badge badge-amber"><AlertTriangle size={10} /> Error</span>;
  return null;
}

// ─── Change history modal ─────────────────────────────────────────────────────
function ChangeHistoryModal({ list, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/bundles/lists/${list.id}/history`)
      .then(r => setHistory(r.data.history))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [list.id]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '640px',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>Change History</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{list.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}><span className="spinner" /></div>
          ) : !history || history.length === 0 ? (
            <div className="empty-state"><div className="empty-state-text">No changes recorded yet</div></div>
          ) : history.map((entry, i) => {
            // Support both old format {action, bundleIds, timestamp} and new DB format {bundle_id, timestamp}
            const bundleIds = entry.bundleIds || [entry.bundle_id].filter(Boolean);
            const ts = entry.timestamp || entry.modified_at;
            return (
              <div key={i} style={{ padding: '10px 12px', marginBottom: '8px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {entry.action && <ActionBadge action={entry.action} />}
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{bundleIds.length} bundle{bundleIds.length !== 1 ? 's' : ''}</span>
                    {entry.notes && <span style={{ fontSize: '11px', color: 'var(--cyan)' }}>{entry.notes}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(bundleIds.join('\n'));
                        toast.success(`Copied ${bundleIds.length} bundle ID${bundleIds.length !== 1 ? 's' : ''}`);
                      }}
                      style={{ fontSize: '10px', color: 'var(--cyan)', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: '3px', padding: '2px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Copy size={9}/> Copy all
                    </button>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{formatDate(ts)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {bundleIds.slice(0, 20).map((bid, bi) => (
                    <code key={bi} style={{ fontSize: '10px', background: 'var(--bg-base)', padding: '1px 6px', borderRadius: '3px', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{bid}</code>
                  ))}
                  {bundleIds.length > 20 && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>+{bundleIds.length - 20} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Mandatory notes modal ────────────────────────────────────────────────────
// Blocks an add/remove action until the user enters a non-empty note/reason.
// Controlled via `prompt` = { title, label, prefill, confirmLabel, danger, onConfirm(note) }.
function NotesModal({ prompt, onClose }) {
  const [note, setNote] = React.useState(prompt?.prefill || '');
  const [submitting, setSubmitting] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    setNote(prompt?.prefill || '');
    setSubmitting(false); // reset — otherwise a second action inherits stale submitting=true and sticks on "Working..."
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [prompt]);

  if (!prompt) return null;
  const trimmed = note.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  async function confirm() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await prompt.onConfirm(trimmed);
      onClose();
    } catch {
      // onConfirm handles its own error toasts; keep the modal open so the
      // user doesn't lose their typed note on a transient failure.
      setSubmitting(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
         onMouseDown={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', width: '100%', maxWidth: '460px', boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>{prompt.title}</div>
          <button onClick={() => !submitting && onClose()} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex' }}><X size={16} /></button>
        </div>
        <div style={{ padding: '18px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
            {prompt.label || 'Reason / note'} <span style={{ color: 'var(--red)' }}>*</span>
          </label>
          <textarea
            ref={inputRef}
            className="input-field"
            style={{ width: '100%', minHeight: '80px', resize: 'vertical', fontSize: '13px', lineHeight: 1.6 }}
            placeholder="e.g. Added per DV360 IO #4521 / Removed — flagged for fraud by partner"
            value={note}
            onChange={e => setNote(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) confirm(); }}
          />
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
            This note is required and will appear in the list's Change History.
          </div>
        </div>
        <div style={{ padding: '14px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => !submitting && onClose()} disabled={submitting}>Cancel</button>
          <button className={`btn btn-sm ${prompt.danger ? 'btn-danger' : 'btn-primary'}`} onClick={confirm} disabled={!canSubmit}>
            {submitting
              ? <><RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> Working...</>
              : (prompt.confirmLabel || 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Recent changes cell ──────────────────────────────────────────────────────
function RecentChangesCell({ changes }) {
  const [open, setOpen] = useState(false);
  if (!changes || changes.length === 0) return <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>;
  const latest = changes[0];
  // Normalise: DB rows arrive as { bundleIds, timestamp } but guard defensively
  const latestIds = latest.bundleIds || (latest.bundle_id ? [latest.bundle_id] : []);
  if (latestIds.length === 0) return <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>;
  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        {latest.action && <ActionBadge action={latest.action} />}
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{latestIds.length} bundle{latestIds.length !== 1 ? 's' : ''}</span>
        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{timeAgo(latest.timestamp || latest.modified_at)}</span>
        {changes.length > 1 && (open ? <ChevronDown size={11} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={11} style={{ color: 'var(--text-muted)' }} />)}
      </div>
      {open && changes.slice(0, 5).map((c, i) => {
        const ids = c.bundleIds || (c.bundle_id ? [c.bundle_id] : []);
        return (
          <div key={i} style={{ display: 'flex', gap: '6px', padding: '3px 0', borderBottom: '1px solid var(--border)', fontSize: '11px' }}>
            {c.action && <ActionBadge action={c.action} />}
            <span style={{ color: 'var(--text-muted)' }}>{ids.length} bundle{ids.length !== 1 ? 's' : ''}</span>
            <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>{timeAgo(c.timestamp || c.modified_at)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Client-side platform detection (mirrors backend detectPlatform) ──────────
function detectPlatform(bundleId) {
  if (!bundleId) return 'Unknown';
  const b = bundleId.trim();
  if (/^\d+$/.test(b)) return 'Apple App Store';
  if (b.includes('.')) {
    const parts = b.split('.');
    const first = parts[0].toLowerCase();
    const last  = parts[parts.length - 1].toLowerCase();
    const gpLeadTlds = ['com', 'org', 'net', 'io', 'co', 'in', 'tv', 'me', 'app', 'games', 'mobi'];
    if (gpLeadTlds.includes(first)) return 'Google Play';
    const webTlds = ['com', 'net', 'org', 'io', 'co', 'tv', 'me', 'app', 'info', 'biz', 'edu', 'gov', 'uk', 'de', 'fr', 'jp'];
    if (webTlds.includes(last)) return 'Web / Other';
    if (parts.length >= 3 && /^[a-z0-9.]+$/i.test(b)) return 'Google Play';
    return 'Web / Other';
  }
  return 'Unknown';
}

// ─── CTV platform detection (for Bundle Tracker CTV lists) ───────────────────
function detectCTVPlatform(bundleId) {
  if (!bundleId) return 'CTV';
  const b = bundleId.trim();
  if (/^B0[A-Z0-9]{8}$/i.test(b)) return 'Fire TV';
  if (/^[Gg]\d+$/.test(b))         return 'Samsung';
  if (/^\d+$/.test(b) && b.length <= 8) return 'Roku';
  if (/^\d+$/.test(b) && b.length > 8)  return 'Apple TV';
  if (/^[a-z]{2,}\./i.test(b))          return 'Android TV';
  return 'CTV';
}

// ─── Entry validation per list type (soft — warns, does not block) ────────────
function validateEntry(bundleId, listType) {
  const b = bundleId.trim();
  if (!b) return null;
  if (listType === 'domains') {
    // Must look like a domain: has a dot, no path separators, no reverse-domain leading TLD
    const domainRe = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    if (!domainRe.test(b)) return `"${b}" doesn't look like a domain (e.g. publisher.com)`;
    const gpTlds = ['com', 'org', 'net', 'io', 'co', 'in', 'tv', 'me'];
    if (gpTlds.includes(b.split('.')[0].toLowerCase())) return `"${b}" looks like an app bundle ID, not a domain`;
  } else if (listType === 'inapp') {
    const isIOS = /^\d+$/.test(b);
    const isAndroid = /^[a-z]{2,}\./i.test(b);
    if (!isIOS && !isAndroid) return `"${b}" doesn't look like an iOS (numeric) or Android (reverse-domain) bundle ID`;
  } else if (listType === 'ctv') {
    const isRoku    = /^\d+$/.test(b) && b.length <= 8;
    const isAppleTV = /^\d+$/.test(b) && b.length > 8;
    const isFireTV  = /^B0[A-Z0-9]{8}$/i.test(b);
    const isSamsung = /^[Gg]\d+$/.test(b);
    const isAndTV   = /^[a-z]{2,}\./i.test(b);
    if (!isRoku && !isAppleTV && !isFireTV && !isSamsung && !isAndTV)
      return `"${b}" doesn't match any known CTV bundle format (Roku numeric, Fire TV ASIN, Samsung G-prefix, Apple TV/Android TV)`;
  }
  return null;
}
async function checkBundleOnStore(bundleId) {
  try {
    const res = await axios.post('/api/bundle-lookup/lookup', { bundleIds: [bundleId] });
    return res.data?.results?.[0]?.success ? 'live' : 'not_found';
  } catch {
    return 'error';
  }
}

// ─── Export helpers ───────────────────────────────────────────────────────────
function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

async function exportList(listId, listName, format, listType = 'inapp') {
  try {
    const { data } = await axios.get('/api/bundles', {
      params: { listId, limit: 9999 }
    });
    const items = data.bundles || [];
    if (items.length === 0) {
      toast('No entries in this list to export', { icon: 'ℹ️' });
      return;
    }
    const safeName = listName.replace(/[^a-z0-9_\-]/gi, '_');
    if (format === 'txt') {
      const content = items.map(b => b.bundleId).join('\n');
      downloadFile(content, `${safeName}.txt`, 'text/plain');
    } else {
      const isDomains = listType === 'domains';
      let rows;
      if (isDomains) {
        // Domains lists: only Domain + ads.txt URL
        rows = [
          ['Domain', 'ads.txt URL'],
          ...items.map(b => {
            const domain = String(b.bundleId || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '');
            return [domain, domain ? `https://${domain}/ads.txt` : ''];
          })
        ];
      } else {
        // In-App / CTV lists: full column set
        rows = [
          ['Bundle ID', 'App Name', 'Platform', 'Store URL', 'Developer Domain', 'app-ads.txt URL'],
          ...items.map(b => {
            const dom = b.developerDomain ? String(b.developerDomain).replace(/^https?:\/\//, '') : '';
            return [
              b.bundleId,
              b.appName  || '',
              b.platform || '',
              b.storeUrl || '',
              dom,
              dom ? `https://${dom}/app-ads.txt` : '',
            ];
          })
        ];
      }
      const csv = rows.map(r =>
        r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      downloadFile(csv, `${safeName}.csv`, 'text/csv');
    }
    toast.success(`Exported ${items.length} entr${items.length === 1 ? 'y' : 'ies'}`);
  } catch {
    toast.error('Export failed');
  }
}

// ─── Export check-results table as CSV ───────────────────────────────────────
// Export check results in LONG format: one row per bundle x list membership,
// so users can filter list-wise in Excel. Operates on the passed (filtered) rows.
function exportCheckResultsCsv(rows, checkStoreStatuses = {}, idLabel = 'Bundle ID') {
  if (!rows?.length) {
    toast('No results to export', { icon: 'i' });
    return;
  }
  const hasStoreCol = Object.keys(checkStoreStatuses).length > 0;
  const headers = [idLabel, 'Platform', 'Status', 'List'];
  if (hasStoreCol) headers.push('Store Status');
  const out = [];
  rows.forEach(r => {
    const entries = [];
    (r.approvedAcross || []).forEach(n => entries.push(['Approved', n]));
    (r.blockedAcross || []).forEach(n => entries.push(['Blocked', n]));
    if (entries.length === 0) entries.push(['Not in any list', '']);
    entries.forEach(([status, listName]) => {
      const row = [r.bundleId, r.platform || '', status, listName];
      if (hasStoreCol) row.push(checkStoreStatuses[r.bundleId] || '');
      out.push(row);
    });
  });
  const csv = [headers, ...out]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const date = new Date().toISOString().split('T')[0];
  downloadFile(csv, `bundle_check_results_${date}.csv`, 'text/csv');
  toast.success(`Exported ${out.length} row${out.length !== 1 ? 's' : ''}`);
}
function ExportButton({ listId, listName, listType = 'inapp' }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="btn btn-secondary btn-sm"
        onClick={() => setOpen(o => !o)}
        title="Export list">
        <Download size={12} /> Export
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 200,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', minWidth: '150px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.35)', overflow: 'hidden',
        }}>
          {[
            { fmt: 'csv', label: 'Export as CSV',       sub: listType === 'domains' ? 'Domain, ads.txt URL' : 'ID, name, platform, store URL, domain, app-ads.txt' },
            { fmt: 'txt', label: 'Export as Text',       sub: listType === 'domains' ? 'Domains only, one per line' : 'Bundle IDs only, one per line' },
          ].map(({ fmt, label, sub }) => (
            <button key={fmt}
              onClick={() => { exportList(listId, listName, fmt, listType); setOpen(false); }}
              style={{
                display: 'block', width: '100%', padding: '9px 14px',
                textAlign: 'left', background: 'none', border: 'none',
                cursor: 'pointer', borderBottom: '1px solid var(--border)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <div style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 600 }}>{label}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>{sub}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────
function sortRows(rows, col, dir) {
  if (!col || !rows?.length) return rows;
  return [...rows].sort((a, b) => {
    let av = a[col], bv = b[col];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });
}

function SortHeader({ col, label, current, dir, onSort, style = {} }) {
  const active = current === col;
  return (
    <th onClick={() => onSort(col)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      title={`Sort by ${label}`}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <span style={{ fontSize: '9px', color: active ? 'var(--cyan)' : 'var(--text-muted)', lineHeight: 1 }}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BundleTracker() {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState('browse');
  const [searchQ, setSearchQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [storeStatuses, setStoreStatuses] = useState({});
  const [checkingStore, setCheckingStore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [newBundle, setNewBundle] = useState('');
  const [newAppName, setNewAppName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkListId, setBulkListId] = useState('');

  // ── Mandatory notes modal ──
  // When set, renders the NotesModal. Shape: { title, label, prefill, onConfirm(note) }
  const [notesPrompt, setNotesPrompt] = useState(null);

  const [showBulkDelete, setShowBulkDelete]     = useState(false);
  const [bulkDeleteText, setBulkDeleteText]     = useState('');
  const [bulkDeleteListId, setBulkDeleteListId] = useState('');
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const [checkText, setCheckText] = useState('');
  const [checkResults, setCheckResults] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkStoreStatuses, setCheckStoreStatuses] = useState({});
  const [checkingCheckStore, setCheckingCheckStore] = useState(false);

  // ── Check scope filter ──
  const [checkScope, setCheckScope]           = useState('all');       // 'all' | 'type' | 'specific'
  const [checkScopeType, setCheckScopeType]   = useState('inapp');     // 'domains' | 'inapp' | 'ctv'
  const [checkScopeListIds, setCheckScopeListIds] = useState(new Set()); // for 'specific' mode

  const [newListName, setNewListName] = useState('');
  const [newListType, setNewListType] = useState('inapp'); // list_type: domains|inapp|ctv
  const [newListPurpose, setNewListPurpose] = useState('approved'); // approved (allowlist) | blocked (blocklist)
  const [browseType, setBrowseType]   = useState('inapp'); // which section is active in Browse
  const [historyModal, setHistoryModal] = useState(null);

  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const selectAllRef = React.useRef(null);

  // Sort state — Browse table
  const [sortCol, setSortCol]   = useState('addedAt');
  const [sortDir, setSortDir]   = useState('desc');
  // Sort state — Check Bundles table
  const [checkSortCol, setCheckSortCol] = useState('bundleId');
  const [checkSortDir, setCheckSortDir] = useState('asc');
  const [checkPage, setCheckPage] = useState(1);
  const [checkPageSize, setCheckPageSize] = useState(100);
  const [checkFilterText, setCheckFilterText] = useState('');
  const [checkFilterPlatform, setCheckFilterPlatform] = useState('all');
  const [checkFilterStatus, setCheckFilterStatus] = useState('all');
  const [checkFilterList, setCheckFilterList] = useState('all');
  // Sort state — Create Lists table
  const [listSortCol, setListSortCol] = useState('name');
  const [listSortDir, setListSortDir] = useState('asc');
  // Edit row state
  const [editRow, setEditRow]           = useState(null);
  const [editDraft, setEditDraft]       = useState({});
  const [editSaving, setEditSaving]     = useState(false);
  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  // DSP Lines tab state
  const [dspLines, setDspLines]   = useState([]);
  const [dspDraft, setDspDraft]   = useState({});
  const [dspSaving, setDspSaving] = useState(null);
  // ── Change History tab state ──
  const [histRows, setHistRows]               = useState([]);
  const [histTotal, setHistTotal]             = useState(0);
  const [histPage, setHistPage]               = useState(1);
  const [histLimit, setHistLimit]             = useState(50);
  const [histLoading, setHistLoading]         = useState(false);
  const [histListId, setHistListId]           = useState('all');
  const [histAction, setHistAction]           = useState('all');
  const [histFrom, setHistFrom]               = useState('');
  const [histTo, setHistTo]                   = useState('');
  const [histIncludeAuto, setHistIncludeAuto] = useState(false);
  const [histExporting, setHistExporting]     = useState(false);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }
  function handleCheckSort(col) {
    if (checkSortCol === col) setCheckSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCheckSortCol(col); setCheckSortDir('asc'); }
  }
  function handleListSort(col) {
    if (listSortCol === col) setListSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setListSortCol(col); setListSortDir('asc'); }
  }

  useEffect(() => { loadLists(); }, []);
  useEffect(() => {
    if (selectedList) {
      setPage(1);
      loadBundles(1);
      setStoreStatuses({});
      setSelectedIds(new Set());
    }
  }, [selectedList, searchQ]);

  // ── Change History tab: (re)load when active or when filters change ──
  useEffect(() => {
    if (tab === 'history') loadHistory(1);
  }, [tab, histListId, histAction, histFrom, histTo, histIncludeAuto]);

  function buildHistoryParams(extra = {}) {
    const p = { ...extra };
    if (histListId && histListId !== 'all') p.listId = histListId;
    if (histAction && histAction !== 'all') p.action = histAction;
    if (histFrom) p.from = histFrom;
    if (histTo) p.to = histTo;
    if (histIncludeAuto) p.includeAuto = 1;
    return p;
  }

  async function loadHistory(pageNum = histPage, size = histLimit) {
    setHistLoading(true);
    try {
      const { data } = await axios.get('/api/bundles/history', {
        params: buildHistoryParams({ page: pageNum, limit: size }),
      });
      setHistRows(data.entries || []);
      setHistTotal(data.total || 0);
      setHistPage(pageNum);
    } catch {
      toast.error('Failed to load change history');
    } finally {
      setHistLoading(false);
    }
  }

  async function exportHistoryCsv() {
    setHistExporting(true);
    try {
      const { data } = await axios.get('/api/bundles/history', {
        params: buildHistoryParams({ format: 'csv' }),
      });
      const entries = data.entries || [];
      if (entries.length === 0) { toast('No history rows to export', { icon: 'i' }); return; }
      const ACTION = { added: 'Added', removed: 'Removed', bulk_added: 'Bulk Added', bulk_removed: 'Bulk Removed', list_created: 'List Created', list_deleted: 'List Deleted', purpose_changed: 'Purpose Changed', env_changed: 'Environment Changed' };
      const header = ['Timestamp (UTC)', 'List', 'List ID', 'Action', 'Bundle Count', 'Bundle IDs', 'Notes'];
      const rows = [header, ...entries.map(e => [
        e.timestamp ? new Date(e.timestamp).toISOString() : '',
        e.listName || '',
        e.listId || '',
        ACTION[e.action] || e.action || '',
        e.bundleCount != null ? e.bundleCount : (e.bundleIds ? e.bundleIds.length : 0),
        (e.bundleIds || []).join(' '),
        e.notes || '',
      ])];
      const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
      downloadFile(csv, `bundle-change-history_${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
      if (data.capped) toast('Export capped at 100,000 rows', { icon: 'warning' });
      toast.success(`Exported ${entries.length} row${entries.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('History export failed');
    } finally {
      setHistExporting(false);
    }
  }

  async function loadLists() {
    try {
      const { data } = await axios.get('/api/bundles/lists');
      setLists(data);
      // Default the browse panel to All Approved In-App (most common starting point)
      if (!selectedList) {
        const defaultInapp = data.find(l => l.id === 'default-approved-inapp');
        if (defaultInapp) setSelectedList(defaultInapp.id);
        else if (data.length > 0) setSelectedList(data[0].id);
      }
      // Bulk import/delete should never default to a default-approved list
      if (!bulkListId) {
        const firstWritable = data.find(l => !l.id.startsWith('default-approved'));
        if (firstWritable) setBulkListId(firstWritable.id);
      }
      if (!bulkDeleteListId) {
        const firstWritable = data.find(l => !l.id.startsWith('default-approved'));
        if (firstWritable) setBulkDeleteListId(firstWritable.id);
      }
    } catch {}
  }

  async function loadBundles(pageNum = page, size = pageSize) {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/bundles', {
        params: { listId: selectedList, search: searchQ || undefined, limit: size, page: pageNum }
      });
      setBundles(data.bundles);
      setTotal(data.total);
    } catch {} finally { setLoading(false); }
  }

  // Copy every bundle ID (or domain) in a list to the clipboard — quick grab for
  // pasting into a DSP UI without opening the list.
  async function copyListBundles(listId, listName, listType) {
    try {
      const { data } = await axios.get('/api/bundles', { params: { listId, limit: 100000 } });
      const ids = (data.bundles || []).map(b => b.bundleId).filter(Boolean);
      if (ids.length === 0) { toast('No entries to copy', { icon: 'i' }); return; }
      await navigator.clipboard.writeText(ids.join('\n'));
      const noun = listType === 'domains' ? 'domain' : 'bundle ID';
      toast.success(`Copied ${ids.length} ${noun}${ids.length === 1 ? '' : 's'} from "${listName}"`);
    } catch {
      toast.error('Copy failed');
    }
  }

  function goToPage(newPage) {
    const totalPages = Math.ceil(total / pageSize);
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    setStoreStatuses({});     // clear store results — they're page-specific
    setSelectedIds(new Set()); // clear selection on page change
    loadBundles(newPage);
  }

  // NOTE: Manual "Check on Store" was removed from the UI — the daily cron job
  // handles store-status checks automatically, so on-demand buttons were redundant.

  function requestAddBundle() {
    if (!newBundle.trim()) return toast.error('Enter a bundle ID');
    if (!selectedList) return toast.error('Select a list first');
    setNotesPrompt({
      title: 'Add bundle',
      label: `Reason for adding "${newBundle.trim()}"`,
      prefill: newNotes.trim(),
      confirmLabel: 'Add bundle',
      onConfirm: (note) => addBundle(note),
    });
  }

  async function addBundle(note) {
    if (!newBundle.trim()) return toast.error('Enter a bundle ID');
    if (!selectedList) return toast.error('Select a list first');
    // Soft validation based on list type
    const activeListType = lists.find(l => l.id === selectedList)?.listType || 'inapp';
    const warning = validateEntry(newBundle.trim(), activeListType);
    if (warning) toast(warning, { icon: '⚠️', duration: 4000 });
    try {
      await axios.post('/api/bundles', { bundleId: newBundle.trim(), listId: selectedList, listType: browseType, appName: newAppName.trim() || undefined, notes: note });
      toast.success('Bundle added');
      setNewBundle(''); setNewAppName(''); setNewNotes('');
      setPage(1); loadBundles(1); loadLists();
    } catch (e) { toast.error(e.response?.data?.error || 'Error adding bundle'); throw e; }
  }

  function requestBulkImport() {
    const ids = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    if (ids.length === 0) return toast.error('Enter bundle IDs (one per line)');
    if (!selectedList || selectedList.startsWith('default-approved'))
      return toast.error('Select a named list first');
    setNotesPrompt({
      title: 'Bulk import bundles',
      label: `Reason for adding ${ids.length} bundle${ids.length !== 1 ? 's' : ''}`,
      confirmLabel: 'Import',
      onConfirm: (note) => bulkImport(note),
    });
  }

  async function bulkImport(note) {
    const ids = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    if (ids.length === 0) return toast.error('Enter bundle IDs (one per line)');
    if (!selectedList || selectedList.startsWith('default-approved'))
      return toast.error('Select a named list first');
    try {
      const { data } = await axios.post('/api/bundles/bulk', { bundleIds: ids, listId: selectedList, listType: browseType, notes: note });
      toast.success(`Added ${data.summary.added} · Skipped ${data.summary.skipped}`);
      setBulkText(''); setShowBulk(false);
      setPage(1); loadBundles(1); loadLists();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); throw e; }
  }

  function requestBulkDeleteByText() {
    const ids = bulkDeleteText.split('\n').map(l => l.trim()).filter(Boolean);
    if (ids.length === 0) return toast.error('Enter bundle IDs (one per line)');
    setNotesPrompt({
      title: 'Bulk delete bundles',
      label: `Reason for removing ${ids.length} bundle${ids.length !== 1 ? 's' : ''}`,
      confirmLabel: `Delete ${ids.length}`,
      danger: true,
      onConfirm: (note) => bulkDeleteByText(note),
    });
  }

  async function bulkDeleteByText(note) {
    const ids = bulkDeleteText.split('\n').map(l => l.trim()).filter(Boolean);
    if (ids.length === 0) return toast.error('Enter bundle IDs (one per line)');
    setBulkDeleteLoading(true);
    try {
      const { data } = await axios.post('/api/bundles/bulk-delete-by-bundleid', { bundleIds: ids, listId: bulkDeleteListId, notes: note });
      toast.success(`Deleted ${data.summary.deleted} · Not found ${data.summary.notFound}`);
      setBulkDeleteText(''); setShowBulkDelete(false);
      setPage(1); loadBundles(1); loadLists();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); throw e; }
    finally { setBulkDeleteLoading(false); }
  }

  async function checkBundles() {
    const ids = checkText.split('\n').map(l => l.trim()).filter(Boolean);
    if (ids.length === 0) return toast.error('Enter bundle IDs');
    setCheckLoading(true); setCheckStoreStatuses({});
    try {
      const payload = { bundleIds: ids };
      if (checkScope === 'type') payload.listType = checkScopeType;
      if (checkScope === 'specific' && checkScopeListIds.size > 0) payload.listIds = [...checkScopeListIds];
      const { data } = await axios.post('/api/bundles/check', payload);
      setCheckResults(data);
      setCheckPage(1); setCheckFilterText(''); setCheckFilterPlatform('all'); setCheckFilterStatus('all'); setCheckFilterList('all');
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setCheckLoading(false); }
  }

  function requestDeleteBundle(id, bundleId) {
    setNotesPrompt({
      title: 'Remove bundle',
      label: `Reason for removing ${bundleId ? `"${bundleId}"` : 'this bundle'}`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: (note) => deleteBundle(id, note),
    });
  }

  async function deleteBundle(id, note) {
    try {
      await axios.delete(`/api/bundles/${id}`, { data: { notes: note } });
      toast.success('Deleted');
      setPage(1); loadBundles(1); loadLists();
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); throw e; }
    finally { setDeleteConfirmId(null); }
  }

  async function saveEdit() {
    if (!editRow) return;
    setEditSaving(true);
    try {
      await axios.put(`/api/bundles/${editRow.id}`, {
        appName:         editDraft.appName?.trim()         ?? null,
        developerDomain: editDraft.developerDomain?.trim() ?? null,
        notes:           editDraft.notes?.trim()           ?? null,
      });
      toast.success('Bundle updated');
      setEditRow(null); setEditDraft({});
      loadBundles(page);
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setEditSaving(false); }
  }

  // ── DSP Lines functions ───────────────────────────────────────────────────
  async function loadDspLines() {
    try {
      const { data } = await axios.get('/api/bundles/dsp-lines');
      setDspLines(data);
      const drafts = {};
      data.forEach(d => { drafts[d.list_id] = { dspName: d.dsp_name, adsTxtLine: d.ads_txt_line }; });
      setDspDraft(prev => ({ ...drafts, ...prev }));
    } catch { toast.error('Failed to load DSP lines'); }
  }

  async function saveDspLine(listId) {
    const draft = dspDraft[listId];
    if (!draft?.dspName?.trim() || !draft?.adsTxtLine?.trim())
      return toast.error('Enter both DSP name and ads.txt line');
    setDspSaving(listId);
    try {
      await axios.post('/api/bundles/dsp-lines', {
        listId, dspName: draft.dspName.trim(), adsTxtLine: draft.adsTxtLine.trim()
      });
      toast.success('DSP line saved');
      loadDspLines();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setDspSaving(null); }
  }

  async function deleteDspLine(listId) {
    try {
      await axios.delete(`/api/bundles/dsp-lines/${listId}`);
      toast.success('DSP line removed');
      setDspDraft(prev => { const n = { ...prev }; delete n[listId]; return n; });
      loadDspLines();
    } catch { toast.error('Delete failed'); }
  }

  // ── Bulk selection helpers ────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds(
      selectedIds.size === bundles.length
        ? new Set()
        : new Set(bundles.map(b => b.id))
    );
  }

  function requestBulkDelete() {
    if (selectedIds.size === 0) return;
    setNotesPrompt({
      title: 'Delete selected bundles',
      label: `Reason for removing ${selectedIds.size} bundle${selectedIds.size !== 1 ? 's' : ''}`,
      confirmLabel: `Delete ${selectedIds.size}`,
      danger: true,
      onConfirm: (note) => bulkDelete(note),
    });
  }

  async function bulkDelete(note) {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post('/api/bundles/bulk-delete', { ids: [...selectedIds], notes: note });
      toast.success(`Deleted ${data.deleted} bundle${data.deleted !== 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      setPage(1); loadBundles(1); loadLists();
    } catch (e) { toast.error(e.response?.data?.error || 'Bulk delete failed'); throw e; }
    finally { setBulkDeleting(false); }
  }

  // Drive the select-all checkbox indeterminate state
  const isAllSelected     = bundles.length > 0 && selectedIds.size === bundles.length;
  const isPartialSelected = selectedIds.size > 0 && selectedIds.size < bundles.length;
  React.useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = isPartialSelected;
  }, [isPartialSelected]);

  async function createList() {
    if (!newListName.trim()) return toast.error('Enter list name');
    try {
      await axios.post('/api/bundles/lists', { name: newListName.trim(), type: newListPurpose, list_type: newListType });
      toast.success(`${newListPurpose === 'blocked' ? 'Blocklist' : 'Allowlist'} created`);
      setNewListName('');
      loadLists();
    } catch (e) { toast.error(e.response?.data?.error || 'Error creating list'); }
  }

  async function changeListPurpose(list, nextType) {
    const current = list.type === 'blocked' ? 'blocked' : 'approved';
    if (nextType === current) return;
    const label = nextType === 'blocked' ? 'Blocklist' : 'Allowlist';
    const note = nextType === 'blocked'
      ? 'Its entries will STOP counting toward your approved totals.'
      : 'Its entries will START counting toward your approved totals.';
    if (!window.confirm(`Change "${list.name}" to a ${label}?\n\n${note}`)) {
      loadLists(); // revert the dropdown to its stored value
      return;
    }
    try {
      await axios.post(`/api/bundles/lists/${list.id}/purpose`, { type: nextType });
      toast.success(`"${list.name}" is now a ${label}`);
      loadLists();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not change purpose');
      loadLists();
    }
  }

  async function deleteList(id) {
    try {
      await axios.delete(`/api/bundles/lists/${id}`);
      toast.success('List deleted');
      if (selectedList === id) setSelectedList(lists.find(l => l.id !== id)?.id || null);
      loadLists();
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  }

  const activeList = lists.find(l => l.id === selectedList);
  const hasStoreResults = Object.keys(storeStatuses).length > 0;
  const hasCheckStoreResults = Object.keys(checkStoreStatuses).length > 0;
  const checkSortedRows = useMemo(() => checkResults ? sortRows(checkResults.results.map(r => ({ ...r, approvedCount: r.approvedAcross?.length || 0, existsCount: (r.approvedAcross?.length || 0) + (r.blockedAcross?.length || 0) })), checkSortCol, checkSortDir) : [], [checkResults, checkSortCol, checkSortDir]);
  const checkListOptions = useMemo(() => { const set = new Set(); (checkResults?.results || []).forEach(r => { (r.approvedAcross || []).forEach(n => set.add(n)); (r.blockedAcross || []).forEach(n => set.add(n)); }); return [...set].sort((a, b) => a.localeCompare(b)); }, [checkResults]);
  const checkPlatformOptions = useMemo(() => { const set = new Set(); (checkResults?.results || []).forEach(r => { if (r.platform) set.add(r.platform); }); return [...set]; }, [checkResults]);
  const checkFilteredRows = useMemo(() => checkSortedRows.filter(r => {
    if (checkFilterText && !String(r.bundleId).toLowerCase().includes(checkFilterText.trim().toLowerCase())) return false;
    if (checkFilterPlatform !== 'all' && r.platform !== checkFilterPlatform) return false;
    if (checkFilterStatus === 'approved' && !(r.approvedAcross?.length > 0)) return false;
    if (checkFilterStatus === 'blocked' && !(r.blockedAcross?.length > 0)) return false;
    if (checkFilterStatus === 'notin' && ((r.approvedAcross?.length > 0) || (r.blockedAcross?.length > 0))) return false;
    if (checkFilterList !== 'all' && !((r.approvedAcross || []).includes(checkFilterList) || (r.blockedAcross || []).includes(checkFilterList))) return false;
    return true;
  }), [checkSortedRows, checkFilterText, checkFilterPlatform, checkFilterStatus, checkFilterList]);
  const checkHasDomains = checkPlatformOptions.includes('Web / Other');
  const checkAllDomains = !!checkResults && checkResults.results.length > 0 && checkResults.results.every(r => r.platform === 'Web / Other');
  const checkIdLabel = checkAllDomains ? 'Domain' : checkHasDomains ? 'Bundle ID / Domain' : 'Bundle ID';
  const checkTotalPages = Math.max(1, Math.ceil(checkFilteredRows.length / checkPageSize));
  const checkSafePage = Math.min(checkPage, checkTotalPages);
  const checkPageRows = checkFilteredRows.slice((checkSafePage - 1) * checkPageSize, (checkSafePage - 1) * checkPageSize + checkPageSize);

  return (
    <div>
      <h1 className="page-title">Bundle Tracker</h1>
      <p className="page-subtitle">Manage approved app bundle lists across DSPs, bulk import, and status checks</p>

      <div className="tabs">
        <button className={`tab ${tab === 'browse' ? 'active' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} onClick={() => setTab('browse')}><Package size={13} /> Browse & Manage</button>
        <button className={`tab ${tab === 'check' ? 'active' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} onClick={() => setTab('check')}><CheckCircle size={13} /> Check Bundles</button>
        <button className={`tab ${tab === 'lists' ? 'active' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} onClick={() => setTab('lists')}><Plus size={13} /> Create Lists</button>
        <button className={`tab ${tab === 'dsplines' ? 'active' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} onClick={() => { setTab('dsplines'); loadDspLines(); }}><Tag size={13} /> DSP Lines</button>
        <button className={`tab ${tab === 'history' ? 'active' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} onClick={() => setTab('history')}><History size={13} /> Change History</button>
      </div>

      {/* ── BROWSE & MANAGE ── */}
      {tab === 'browse' && (
        <div>
          {/* Type selector */}
          <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
            {[
              { key: 'domains', label: 'Domains',          color: 'var(--cyan)'   },
              { key: 'inapp',   label: 'In-App Bundles',   color: 'var(--green)'  },
              { key: 'ctv',     label: 'CTV Bundles',      color: 'var(--purple)' },
            ].map(t => (
              <button key={t.key}
                className={`btn btn-sm ${browseType === t.key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => {
                  setBrowseType(t.key);
                  // Auto-select All Approved list for this type
                  const defaultId = `default-approved-${t.key}`;
                  const def = lists.find(l => l.id === defaultId);
                  if (def) setSelectedList(def.id);
                }}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '16px', alignItems: 'start' }}>
            {/* Sidebar — lists of current type */}
            <div>
              {/* All Approved header for this type */}
              {(() => {
                const defaultId = `default-approved-${browseType}`;
                const defaultList = lists.find(l => l.id === defaultId);
                const typeLabel = browseType === 'domains' ? 'All Approved Domains' : browseType === 'ctv' ? 'All Approved CTV Bundles' : 'All Approved In-App Bundles';
                return (
                  <div onClick={() => defaultList && setSelectedList(defaultId)}
                    style={{
                      padding: '10px 12px', marginBottom: '8px', borderRadius: 'var(--radius-sm)',
                      background: selectedList === defaultId ? 'color-mix(in srgb, var(--green) 10%, transparent)' : 'var(--bg-card)',
                      border: `1px solid ${selectedList === defaultId ? 'color-mix(in srgb, var(--green) 35%, transparent)' : 'color-mix(in srgb, var(--green) 20%, transparent)'}`,
                      cursor: 'pointer', fontSize: '12px'
                    }}>
                    <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: '3px', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ✦ {typeLabel}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '10px', textTransform: 'uppercase' }}>auto-managed</span>
                      <span className="badge badge-muted">{defaultList?.count ?? 0}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Divider */}
              <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', padding: '4px 2px 6px', fontWeight: 700 }}>
                Your Lists
              </div>

              {/* User lists of this type */}
              {lists.filter(l => l.listType === browseType && !l.id.startsWith('default-approved')).length === 0 && (
                <div style={{ padding: '12px', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                  No lists yet — create one in the Create Lists tab
                </div>
              )}
              {lists.filter(l => l.listType === browseType && !l.id.startsWith('default-approved')).map(l => (
                <div key={l.id} onClick={() => setSelectedList(l.id)} style={{
                  padding: '10px 12px', marginBottom: '4px', borderRadius: 'var(--radius-sm)',
                  background: selectedList === l.id ? 'var(--cyan-dim)' : 'var(--bg-card)',
                  border: `1px solid ${selectedList === l.id ? 'color-mix(in srgb, var(--cyan) 30%, transparent)' : 'var(--border)'}`,
                  cursor: 'pointer', fontSize: '12px'
                }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: l.type === 'blocked' ? 'var(--red)' : 'var(--green)', fontSize: '10px', textTransform: 'uppercase' }}>{l.type === 'blocked' ? 'blocked' : 'approved'}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className="badge badge-muted">{l.count}</span>
                      <button
                        onClick={e => { e.stopPropagation(); copyListBundles(l.id, l.name, l.listType); }}
                        title={`Copy all ${l.listType === 'domains' ? 'domains' : 'bundle IDs'}`}
                        style={{ background: 'none', border: 'none', padding: '2px', cursor: 'pointer', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center' }}>
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Main panel */}
            <div>
              {/* Add bundle — hidden when viewing an All Approved default list */}
              {activeList && !activeList.id.startsWith('default-approved') ? (
                <div className="card" style={{ marginBottom: '12px' }}>
                  <div className="card-title" style={{ marginBottom: '10px' }}>
                    <Plus size={13} /> Add to &ldquo;{activeList?.name}&rdquo;
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input className="input-field"
                      placeholder={
                        activeList?.listType === 'domains' ? 'Domain (e.g. publisher.com)' :
                        activeList?.listType === 'ctv'     ? 'CTV Bundle ID (e.g. 13535, B00QXKFNK2)' :
                        'Bundle ID (e.g. com.example.app)'
                      }
                      value={newBundle} onChange={e => setNewBundle(e.target.value)} onKeyDown={e => e.key === 'Enter' && requestAddBundle()} />
                    <input className="input-field" placeholder="App / site name (optional)" value={newAppName} onChange={e => setNewAppName(e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input className="input-field" placeholder="Notes (optional)" value={newNotes} onChange={e => setNewNotes(e.target.value)} style={{ flex: 1 }} />
                    <button className="btn btn-primary" onClick={requestAddBundle}><Plus size={13} /> Add</button>
                    <button className="btn btn-primary" onClick={() => { setShowBulk(o => !o); setShowBulkDelete(false); }}><Upload size={13} /> Bulk Import</button>
                    <button className="btn btn-danger" onClick={() => { setShowBulkDelete(o => !o); setShowBulk(false); }}><Trash2 size={13} /> Bulk Delete</button>
                  </div>
                </div>
              ) : activeList?.id.startsWith('default-approved') ? (
                <div className="alert alert-info" style={{ marginBottom: '12px' }}>
                  <Info size={13} />
                  <div>This is a read-only aggregate list — it automatically reflects all entries across your approved lists of this type. To add or remove entries, use one of your named lists on the left.</div>
                </div>
              ) : null}

            {/* Bulk import panel — only for named lists */}
            {showBulk && activeList && !activeList.id.startsWith('default-approved') && (
              <div className="card" style={{ marginBottom: '12px', borderColor: 'color-mix(in srgb, var(--cyan) 25%, transparent)' }}>
                <div className="card-title" style={{ marginBottom: '10px' }}><Upload size={13} /> Bulk Import</div>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Target List</label>
                  <select className="select-field w-full" value={bulkListId} onChange={e => setBulkListId(e.target.value)}>
                    {lists.filter(l => l.listType === browseType && !l.id.startsWith('default-approved')).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <textarea className="input-field w-full" style={{ minHeight: '140px', fontSize: '12px' }}
                  placeholder={
                    browseType === 'domains' ? 'publisher1.com\npublisher2.com\n...' :
                    browseType === 'ctv'     ? '13535\nB00QXKFNK2\ncom.netflix.ninja\n...' :
                    'com.example.app1\ncom.example.app2\n1234567890\n...'
                  }
                  value={bulkText} onChange={e => setBulkText(e.target.value)} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{bulkText.split('\n').filter(l => l.trim()).length} entries</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setShowBulk(false); setBulkText(''); }}>Cancel</button>
                    <button className="btn btn-primary" onClick={requestBulkImport}><Upload size={13} /> Import</button>
                  </div>
                </div>
              </div>
            )}

            {/* Bulk delete panel — only for named lists */}
            {showBulkDelete && activeList && !activeList.id.startsWith('default-approved') && (
              <div className="card" style={{ marginBottom: '12px', borderColor: 'color-mix(in srgb, var(--red) 25%, transparent)' }}>
                <div className="card-title" style={{ marginBottom: '10px', color: 'var(--red)' }}><Trash2 size={13} /> Bulk Delete</div>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block' }}>Source List</label>
                  <select className="select-field w-full" value={bulkDeleteListId} onChange={e => setBulkDeleteListId(e.target.value)}>
                    {lists.filter(l => l.listType === browseType && !l.id.startsWith('default-approved')).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <textarea className="input-field w-full" style={{ minHeight: '140px', fontSize: '12px', borderColor: 'color-mix(in srgb, var(--red) 20%, transparent)' }}
                  placeholder={'Paste bundle IDs to remove (one per line)\n\ncom.example.app1\ncom.example.app2\n1234567890\n...'}
                  value={bulkDeleteText} onChange={e => setBulkDeleteText(e.target.value)} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{bulkDeleteText.split('\n').filter(l => l.trim()).length} bundle IDs</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setShowBulkDelete(false); setBulkDeleteText(''); }}>Cancel</button>
                    <button className="btn btn-danger" onClick={requestBulkDeleteByText} disabled={bulkDeleteLoading}>
                      {bulkDeleteLoading
                        ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Deleting...</>
                        : <><Trash2 size={13} /> Delete</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bundles table */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div className="card-title" style={{ margin: 0 }}>
                    <Package size={13} /> Bundles <span className="badge badge-muted">{total}</span>
                  </div>
                  {selectedIds.size > 0 && (
                    <button className="btn btn-danger btn-sm" onClick={requestBulkDelete} disabled={bulkDeleting}>
                      {bulkDeleting
                        ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Deleting...</>
                        : <><Trash2 size={12} /> Delete {selectedIds.size} selected</>}
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input className="input-field" style={{ width: '180px' }} placeholder="Search..." value={searchQ} onChange={e => setSearchQ(e.target.value)} />
                  {activeList && (
                    <ExportButton listId={activeList.id} listName={activeList.name} listType={activeList.listType || browseType} />
                  )}
                </div>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '30px' }}><span className="spinner" /></div>
              ) : bundles.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon"><Package size={20} style={{ color: 'var(--text-muted)' }} /></div>
                  <div className="empty-state-text">No bundles in this list</div>
                  <div className="empty-state-sub">Add bundles above or use Bulk Import</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <thead>
                      <tr>
                        {!activeList?.id.startsWith('default-approved') && (
                          <th style={{ width: '32px' }}>
                            <input type="checkbox" ref={selectAllRef} checked={isAllSelected} onChange={toggleSelectAll}
                              style={{ cursor: 'pointer', accentColor: 'var(--cyan)' }} />
                          </th>
                        )}
                        <SortHeader col="bundleId"        label="Bundle ID"        current={sortCol} dir={sortDir} onSort={handleSort} style={{ width: '200px' }} />
                        <SortHeader col="appName"         label="App Name"         current={sortCol} dir={sortDir} onSort={handleSort} style={{ width: '180px' }} />
                        <SortHeader col="platform"        label="Platform"         current={sortCol} dir={sortDir} onSort={handleSort} style={{ width: '100px' }} />
                        <SortHeader col="developerDomain" label="App-ads.txt URL"  current={sortCol} dir={sortDir} onSort={handleSort} style={{ width: '260px' }} />
                        <SortHeader col="privacyPolicyDomain" label="Privacy Policy"   current={sortCol} dir={sortDir} onSort={handleSort} style={{ width: '180px' }} />
                        <SortHeader col="addedAt"         label="Added"            current={sortCol} dir={sortDir} onSort={handleSort} style={{ width: '90px' }} />
                        {hasStoreResults && <th style={{ width: '100px' }}>Store Status</th>}
                        {!activeList?.id.startsWith('default-approved') && <th style={{ width: '70px' }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortRows(bundles, sortCol, sortDir).map(b => {
                        const isEditing    = editRow?.id === b.id;
                        const isDelConfirm = deleteConfirmId === b.id;
                        const appAdsTxtUrl = b.developerDomain
                          ? `https://${b.developerDomain.replace(/^https?:\/\//, '')}/app-ads.txt`
                          : null;
                        return (
                        <tr key={b.id} style={selectedIds.has(b.id) ? { background: 'color-mix(in srgb, var(--cyan) 6%, transparent)' } : {}}>
                          {!activeList?.id.startsWith('default-approved') && (
                            <td>
                              <input type="checkbox" checked={selectedIds.has(b.id)} onChange={() => toggleSelect(b.id)}
                                style={{ cursor: 'pointer', accentColor: 'var(--cyan)' }} />
                            </td>
                          )}
                          <td className="mono" title={b.bundleId}
                            style={{ color: 'var(--cyan)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                            {b.bundleId}
                          </td>
                          {/* App Name — editable */}
                          <td title={b.appName || ''} style={{ color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isEditing
                              ? <input className="input-field" style={{ fontSize:'11px', padding:'3px 6px', width:'100%' }}
                                  value={editDraft.appName ?? (b.appName || '')}
                                  onChange={e => setEditDraft(d => ({ ...d, appName: e.target.value }))} />
                              : b.appName || '—'}
                          </td>
                          <td><PlatformCell platform={b.platform} bundleId={b.bundleId} listType={activeList?.listType} /></td>
                          {/* App-ads.txt — editable developer domain */}
                          <td style={{ overflow: 'hidden' }}>
                            {isEditing
                              ? <input className="input-field" style={{ fontSize:'11px', padding:'3px 6px', width:'100%', fontFamily:'var(--font-mono)' }}
                                  placeholder="developer domain (e.g. example.com)"
                                  value={editDraft.developerDomain ?? (b.developerDomain || '')}
                                  onChange={e => setEditDraft(d => ({ ...d, developerDomain: e.target.value }))} />
                              : appAdsTxtUrl
                              ? <a href={appAdsTxtUrl} target="_blank" rel="noopener noreferrer" title={appAdsTxtUrl}
                                  style={{ fontSize:'10px', color:'var(--cyan)', textDecoration:'none', display:'flex', alignItems:'center', gap:'4px', fontFamily:'var(--font-mono)', overflow:'hidden' }}>
                                  <ExternalLink size={9} style={{ flexShrink:0 }}/>
                                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.developerDomain.replace(/^https?:\/\//, '')}</span>
                                </a>
                              : <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>—</span>}
                          </td>
                          {/* Privacy Policy — app-ads.txt found on the app's privacy-policy domain */}
                          <td style={{ overflow: 'hidden' }}>
                            {b.privacyPolicyDomain ? (
                              <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
                                <a href={`https://${b.privacyPolicyDomain}/app-ads.txt`} target="_blank" rel="noopener noreferrer"
                                   title={`https://${b.privacyPolicyDomain}/app-ads.txt`}
                                   style={{ fontSize:'10px', color:'var(--cyan)', textDecoration:'none', display:'flex', alignItems:'center', gap:'4px', fontFamily:'var(--font-mono)', overflow:'hidden' }}>
                                  <ExternalLink size={9} style={{ flexShrink:0 }}/>
                                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.privacyPolicyDomain}</span>
                                </a>
                                {b.resolvedViaPrivacy && (
                                  <span title="app-ads.txt was found on the privacy-policy domain, not the developer domain"
                                        style={{ fontSize:'8px', fontWeight:700, color:'var(--cyan)', border:'1px solid var(--cyan)', borderRadius:'3px', padding:'0 4px', alignSelf:'flex-start', whiteSpace:'nowrap' }}>
                                    app-ads.txt found here
                                  </span>
                                )}
                              </div>
                            ) : <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>—</span>}
                          </td>
                          <td className="text-muted">{timeAgo(b.addedAt)}</td>
                          {hasStoreResults && <td><StoreStatusCell status={storeStatuses[b.bundleId]} /></td>}
                          {!activeList?.id.startsWith('default-approved') && (
                            <td>
                              {isDelConfirm ? (
                                <div style={{ display:'flex', gap:'3px', alignItems:'center' }}>
                                  <span style={{ fontSize:'10px', color:'var(--red)', whiteSpace:'nowrap' }}>Delete?</span>
                                  <button className="btn btn-danger btn-sm" style={{ fontSize:'10px', padding:'2px 7px' }}
                                    onClick={() => deleteBundle(b.id)}>Yes</button>
                                  <button className="btn btn-secondary btn-sm" style={{ fontSize:'10px', padding:'2px 7px' }}
                                    onClick={() => setDeleteConfirmId(null)}>No</button>
                                </div>
                              ) : isEditing ? (
                                <div style={{ display:'flex', gap:'3px' }}>
                                  <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={editSaving}
                                    style={{ padding:'3px 8px' }} title="Save">
                                    {editSaving ? <RefreshCw size={10} style={{ animation:'spin 1s linear infinite' }}/> : <Check size={10}/>}
                                  </button>
                                  <button className="btn btn-secondary btn-sm" style={{ padding:'3px 8px' }} title="Cancel"
                                    onClick={() => { setEditRow(null); setEditDraft({}); }}>
                                    <XCircle size={10}/>
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display:'flex', gap:'3px' }}>
                                  <button className="btn btn-secondary btn-sm" style={{ padding:'3px 8px' }} title="Edit"
                                    onClick={() => { setEditRow(b); setEditDraft({ appName: b.appName || '', developerDomain: b.developerDomain || '' }); }}>
                                    <Pencil size={10}/>
                                  </button>
                                  <button className="btn btn-danger btn-sm" style={{ padding:'3px 8px' }} title="Delete"
                                    onClick={() => requestDeleteBundle(b.id, b.bundleId)}>
                                    <Trash2 size={10}/>
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Pagination ── */}
              {total > 0 && (() => {
                const totalPages = Math.ceil(total / pageSize);
                const start = (page - 1) * pageSize + 1;
                const end   = Math.min(page * pageSize, total);
                return (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        Showing {start}–{end} of {total}
                      </span>
                      <select className="select-field" style={{ fontSize: '11px', padding: '2px 6px' }} value={pageSize}
                        onChange={e => { const s = parseInt(e.target.value); setPageSize(s); setPage(1); setStoreStatuses({}); setSelectedIds(new Set()); loadBundles(1, s); }}>
                        {[50, 100, 250, 500].map(n => <option key={n} value={n}>{n} / page</option>)}
                      </select>
                    </div>
                    {totalPages > 1 && (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => goToPage(1)} disabled={page === 1 || loading}>«</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => goToPage(page - 1)} disabled={page === 1 || loading}>‹ Prev</button>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', padding: '0 8px' }}>
                        Page {page} of {totalPages}
                      </span>
                      <button className="btn btn-secondary btn-sm" onClick={() => goToPage(page + 1)} disabled={page === totalPages || loading}>Next ›</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => goToPage(totalPages)} disabled={page === totalPages || loading}>»</button>
                    </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ── CHECK BUNDLES TAB ── */}
      {tab === 'check' && (
        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            {/* Title updates dynamically based on scope */}
            <div className="card-title" style={{ marginBottom: '12px' }}>
              <Search size={13} />
              {checkScope === 'all'      && ' Check Bundle Status Across All Lists'}
              {checkScope === 'type'     && ` Check Across ${checkScopeType === 'domains' ? 'Domain' : checkScopeType === 'ctv' ? 'CTV' : 'In-App'} Lists Only`}
              {checkScope === 'specific' && ' Check Across Selected Lists'}
            </div>

            {/* ── Scope selector ── */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Check Scope</div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {[
                  { key: 'all',      label: 'All Lists' },
                  { key: 'type',     label: 'By Environment' },
                  { key: 'specific', label: 'Specific Lists' },
                ].map(s => (
                  <button key={s.key}
                    className={`btn btn-sm ${checkScope === s.key ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setCheckScope(s.key); setCheckResults(null); }}>
                    {s.label}
                  </button>
                ))}
              </div>

              {/* By Environment sub-selector */}
              {checkScope === 'type' && (
                <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                  {[
                    { key: 'domains', label: 'Domains' },
                    { key: 'inapp',   label: 'In-App Bundles' },
                    { key: 'ctv',     label: 'CTV Bundles' },
                  ].map(t => (
                    <button key={t.key}
                      className={`btn btn-sm ${checkScopeType === t.key ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => { setCheckScopeType(t.key); setCheckResults(null); }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Specific Lists multi-select */}
              {checkScope === 'specific' && (
                <div style={{ marginTop: '8px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                  {(() => {
                    const userLists = lists.filter(l => !l.id.startsWith('default-approved'));
                    if (userLists.length === 0) return (
                      <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                        No lists created yet — create lists in the Create Lists tab.
                      </div>
                    );
                    return (
                      <>
                        {/* Select all / clear row */}
                        <div style={{ padding: '6px 12px', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)', display: 'flex', gap: '12px' }}>
                          <button className="btn btn-secondary btn-sm" style={{ fontSize: '10px', padding: '2px 8px' }}
                            onClick={() => setCheckScopeListIds(new Set(userLists.map(l => l.id)))}>
                            Select All
                          </button>
                          <button className="btn btn-secondary btn-sm" style={{ fontSize: '10px', padding: '2px 8px' }}
                            onClick={() => setCheckScopeListIds(new Set())}>
                            Clear
                          </button>
                          {checkScopeListIds.size > 0 && (
                            <span style={{ fontSize: '11px', color: 'var(--cyan)', marginLeft: 'auto', alignSelf: 'center' }}>
                              {checkScopeListIds.size} selected
                            </span>
                          )}
                        </div>
                        {/* Group by type */}
                        {['domains', 'inapp', 'ctv'].map(lt => {
                          const group = userLists.filter(l => l.listType === lt);
                          if (!group.length) return null;
                          const typeLabel = lt === 'domains' ? 'Domains' : lt === 'ctv' ? 'CTV Bundles' : 'In-App Bundles';
                          return (
                            <div key={lt}>
                              <div style={{ padding: '5px 12px', fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', background: 'var(--bg-base)', fontWeight: 700 }}>
                                {typeLabel}
                              </div>
                              {group.map(l => (
                                <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '12px' }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                  <input type="checkbox"
                                    checked={checkScopeListIds.has(l.id)}
                                    onChange={() => {
                                      setCheckScopeListIds(prev => {
                                        const next = new Set(prev);
                                        next.has(l.id) ? next.delete(l.id) : next.add(l.id);
                                        return next;
                                      });
                                      setCheckResults(null);
                                    }}
                                    style={{ accentColor: 'var(--cyan)', cursor: 'pointer' }} />
                                  <span style={{ color: 'var(--text-primary)', flex: 1 }}>{l.name}</span>
                                  <span className="badge badge-muted" style={{ fontSize: '10px' }}>{l.count}</span>
                                </label>
                              ))}
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <textarea className="input-field w-full" style={{ minHeight: '150px' }}
              placeholder={'Enter bundle IDs to check (one per line)\n\ncom.example.app1\ncom.example.app2\n1234567890'}
              value={checkText} onChange={e => setCheckText(e.target.value)} />
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button className="btn btn-primary" onClick={checkBundles} disabled={checkLoading ||
                (checkScope === 'specific' && checkScopeListIds.size === 0)}>
                {checkLoading ? <><span className="spinner" /> Checking...</> : <><Search size={13} /> Check</>}
              </button>
              {checkScope === 'specific' && checkScopeListIds.size === 0 && (
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Select at least one list above</span>
              )}
            </div>
          </div>

          {checkResults && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                <div className="stat-card cyan"><div className="stat-label">Total Checked</div><div className="stat-value">{checkResults.summary.total}</div></div>
                <div className="stat-card green">
                  <div className="stat-label">
                    {checkScope === 'all'      && 'Approved (any list)'}
                    {checkScope === 'type'     && `Approved (${checkScopeType === 'domains' ? 'Domain' : checkScopeType === 'ctv' ? 'CTV' : 'In-App'} lists)`}
                    {checkScope === 'specific' && `Approved (selected lists)`}
                  </div>
                  <div className="stat-value green">{checkResults.summary.approved}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Blocked (any list)</div>
                  <div className="stat-value" style={{ color: 'var(--red)' }}>{checkResults.summary.blocked || 0}</div>
                </div>
                <div className="stat-card amber"><div className="stat-label">Not in Scope</div><div className="stat-value amber">{checkResults.summary.notFound}</div></div>
              </div>

              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div className="card-title" style={{ margin: 0 }}>Results</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
<button className="btn btn-secondary btn-sm" onClick={() => { const vals = checkFilteredRows.map(r => r.bundleId); if (!vals.length) return toast('Nothing to copy in current view', { icon: 'i' }); navigator.clipboard.writeText(vals.join('\n')).then(() => toast.success(`Copied ${vals.length} item${vals.length !== 1 ? 's' : ''}`)).catch(() => toast.error('Copy failed')); }}>
                      <Copy size={12} /> Copy IDs / Domains
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => exportCheckResultsCsv(checkFilteredRows, checkStoreStatuses, checkIdLabel)}>
                      <Download size={12} /> Export CSV
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setCheckResults(null); setCheckStoreStatuses({}); setCheckText(''); setCheckPage(1); setCheckFilterText(''); setCheckFilterPlatform('all'); setCheckFilterStatus('all'); setCheckFilterList('all'); }}>
                      <XCircle size={12} /> Clear
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
                  <input className="input-field" placeholder="Filter by ID / domain..." value={checkFilterText} onChange={e => { setCheckFilterText(e.target.value); setCheckPage(1); }} style={{ flex: '1 1 200px', maxWidth: '280px', fontSize: '12px' }} />
                  <select className="select-field" value={checkFilterPlatform} onChange={e => { setCheckFilterPlatform(e.target.value); setCheckPage(1); }} style={{ fontSize: '12px' }}>
                    <option value="all">All platforms</option>
                    {checkPlatformOptions.map(pf => <option key={pf} value={pf}>{({ 'Apple App Store': 'iOS', 'Google Play': 'Android', 'Web / Other': 'Web' })[pf] || pf}</option>)}
                  </select>
                  <select className="select-field" value={checkFilterStatus} onChange={e => { setCheckFilterStatus(e.target.value); setCheckPage(1); }} style={{ fontSize: '12px' }}>
                    <option value="all">All statuses</option>
                    <option value="approved">Approved</option>
                    <option value="blocked">Blocked</option>
                    <option value="notin">Not in any list</option>
                  </select>
                  <select className="select-field" value={checkFilterList} onChange={e => { setCheckFilterList(e.target.value); setCheckPage(1); }} style={{ fontSize: '12px' }} disabled={checkListOptions.length === 0}>
                    <option value="all">All lists</option>
                    {checkListOptions.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: 'auto' }}>{checkFilteredRows.length.toLocaleString()}{checkFilteredRows.length !== checkResults.results.length ? ` of ${checkResults.results.length.toLocaleString()}` : ''} shown</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <SortHeader col="bundleId"     label={checkIdLabel}     current={checkSortCol} dir={checkSortDir} onSort={handleCheckSort} style={{ color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: '0.4px' }} />
                        <SortHeader col="platform"     label="Platform"        current={checkSortCol} dir={checkSortDir} onSort={handleCheckSort} style={{ color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: '0.4px' }} />
                        <SortHeader col="existsCount"  label="Exists In"       current={checkSortCol} dir={checkSortDir} onSort={handleCheckSort} style={{ color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: '0.4px' }} />
                        {hasCheckStoreResults && <th style={{ color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: '0.4px' }}>Store Status</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {checkPageRows.length === 0 && (<tr><td colSpan={hasCheckStoreResults ? 4 : 3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>No results match the current filters.</td></tr>)}
                      {checkPageRows.map((r, i) => (
                        <tr key={i}>
                          <td className="mono" style={{ color: 'var(--text-primary)' }}>{r.bundleId}</td>
                          <td><PlatformCell platform={r.platform} bundleId={r.bundleId} /></td>
                          <td>
                            {(r.approvedAcross?.length > 0 || r.blockedAcross?.length > 0)
                              ? <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                  {(r.approvedAcross || []).map((n, ai) => <span key={`a${ai}`} className="badge badge-green">{n}</span>)}
                                  {(r.blockedAcross || []).map((n, bi) => <span key={`b${bi}`} className="badge badge-red">{n}</span>)}
                                </div>
                              : <span className="badge badge-muted">Not in any list</span>}
                          </td>
                          {hasCheckStoreResults && <td><StoreStatusCell status={checkStoreStatuses[r.bundleId]} /></td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{checkFilteredRows.length === 0 ? 'No rows' : `Showing ${((checkSafePage - 1) * checkPageSize + 1).toLocaleString()}-${Math.min(checkSafePage * checkPageSize, checkFilteredRows.length).toLocaleString()} of ${checkFilteredRows.length.toLocaleString()}`}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <select className="select-field" value={checkPageSize} onChange={e => { setCheckPageSize(parseInt(e.target.value)); setCheckPage(1); }} style={{ fontSize: '12px' }}>
                      <option value={100}>100 / page</option>
                      <option value={250}>250 / page</option>
                      <option value={500}>500 / page</option>
                    </select>
                    <button className="btn btn-secondary btn-sm" disabled={checkSafePage <= 1} onClick={() => setCheckPage(pn => Math.max(1, pn - 1))}><ChevronLeft size={12} /> Prev</button>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '74px', textAlign: 'center' }}>Page {checkSafePage} / {checkTotalPages}</span>
                    <button className="btn btn-secondary btn-sm" disabled={checkSafePage >= checkTotalPages} onClick={() => setCheckPage(pn => Math.min(checkTotalPages, pn + 1))}>Next <ChevronRight size={12} /></button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CREATE LISTS TAB ── */}
      {tab === 'lists' && (
        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-title" style={{ marginBottom: '12px' }}><Plus size={13} /> Create New List</div>
            <div className="alert alert-info" style={{ marginBottom: '12px' }}>
              <Info size={13} />
              <div>
                <strong>Tip:</strong> Lists have an <strong>Environment</strong> (Domains or In-App / Bundle IDs) and a <strong>Purpose</strong> (Allowlist or Blocklist) — pick both. Only <strong>Allowlists</strong> roll up into your "All Approved" totals; <strong>Blocklists</strong> are tracked separately and never inflate them. It helps to mirror both in the name, e.g. <em>"PartnerX In-App Allowlist"</em> or <em>"PartnerY CTV Blocklist"</em>.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input className="input-field" placeholder="e.g. PartnerX In-App Allowlist..." value={newListName} onChange={e => setNewListName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createList()} style={{ flex: 1 }} />
              <select className="select-field" value={newListType} onChange={e => setNewListType(e.target.value)} title="Environment">
                <option value="domains">Domains</option>
                <option value="inapp">In-App Bundles</option>
                <option value="ctv">CTV Bundles</option>
              </select>
              <select className="select-field" value={newListPurpose} onChange={e => setNewListPurpose(e.target.value)} title="Purpose">
                <option value="approved">Allowlist (Approved)</option>
                <option value="blocked">Blocklist (Blocked)</option>
              </select>
              <button className="btn btn-primary" onClick={createList}><Plus size={13} /> Create</button>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: '12px' }}><Package size={13} /> All Lists</div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <SortHeader col="name"       label="Name"     current={listSortCol} dir={listSortDir} onSort={handleListSort} />
                    <SortHeader col="listType"   label="Category" current={listSortCol} dir={listSortDir} onSort={handleListSort} />
                    <th>Purpose</th>
                    <SortHeader col="count"      label="Entries"  current={listSortCol} dir={listSortDir} onSort={handleListSort} />
                    <SortHeader col="createdAt"  label="Created"  current={listSortCol} dir={listSortDir} onSort={handleListSort} />
                    <SortHeader col="modifiedAt" label="Modified" current={listSortCol} dir={listSortDir} onSort={handleListSort} />
                    <th>Recent Changes</th><th>History</th><th>Export</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortRows(lists, listSortCol, listSortDir).map(l => {
                    const isDefault = l.id.startsWith('default-approved');
                    const typeLabel = l.listType === 'domains' ? 'Domains' : l.listType === 'ctv' ? 'CTV' : 'In-App';
                    const typeBadgeClass = l.listType === 'domains' ? 'badge-cyan' : l.listType === 'ctv' ? 'badge-purple' : 'badge-green';
                    return (
                      <tr key={l.id}>
                        <td style={{ fontWeight: 600, color: isDefault ? 'var(--green)' : 'var(--text-primary)' }}>
                          {l.name}
                          {isDefault && <span className="badge badge-green" style={{ marginLeft: '6px', fontSize: '9px' }}>auto</span>}
                        </td>
                        <td><span className={`badge ${typeBadgeClass}`}>{typeLabel}</span></td>
                        <td>
                          {isDefault
                            ? <span className="badge badge-green">Allowlist</span>
                            : <select className="select-field" style={{ fontSize: '11px', padding: '2px 6px' }}
                                value={l.type === 'blocked' ? 'blocked' : 'approved'}
                                onChange={e => changeListPurpose(l, e.target.value)} title="List purpose">
                                <option value="approved">Allowlist</option>
                                <option value="blocked">Blocklist</option>
                              </select>}
                        </td>
                        <td><span className="badge badge-muted">{l.count}</span></td>
                        <td className="text-muted" style={{ fontSize: '11px' }}>{formatDate(l.createdAt)}</td>
                        <td>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{timeAgo(l.modifiedAt)}</span>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{formatDate(l.modifiedAt)}</div>
                        </td>
                        <td style={{ minWidth: '200px' }}><RecentChangesCell changes={l.recentChanges} /></td>
                        <td>
                          <button className="btn btn-secondary btn-sm" onClick={() => setHistoryModal(l)}>
                            <History size={11} /> View
                          </button>
                        </td>
                        <td>
                          <ExportButton listId={l.id} listName={l.name} listType={l.listType} />
                        </td>
                        <td>
                          {!isDefault && (
                            <button className="btn btn-danger btn-sm" onClick={() => deleteList(l.id)}><Trash2 size={11} /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── DSP LINES TAB ── */}
      {tab === 'dsplines' && (
        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-title" style={{ marginBottom: '8px' }}><Tag size={13} /> DSP Lines Configuration</div>
            <div className="alert alert-info" style={{ marginBottom: '0' }}>
              <Info size={13} />
              <div>
                Configure the primary ads.txt / app-ads.txt line for each DSP list. The Bundle Tracker crawls these every day at <strong>5:00 AM UTC</strong> and notifies you if any bundles are missing the line or if apps are no longer live on the store. One primary line per list — use the exact line your DSP requires.
              </div>
            </div>
          </div>

          {lists.filter(l => !l.id.startsWith('default-approved')).length === 0 ? (
            <div className="alert alert-warning">
              <AlertTriangle size={13} />
              <div>No lists created yet. Create lists in the Create Lists tab first, then configure a DSP line for each.</div>
            </div>
          ) : (
            ['domains', 'inapp', 'ctv'].map(lt => {
              const groupLists = lists.filter(l => l.listType === lt && !l.id.startsWith('default-approved'));
              if (!groupLists.length) return null;
              const typeLabel = lt === 'domains' ? 'Domain Lists' : lt === 'ctv' ? 'CTV Bundle Lists' : 'In-App Bundle Lists';
              const fileType  = lt === 'domains' ? 'ads.txt' : 'app-ads.txt';
              return (
                <div key={lt} className="card" style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div className="card-title" style={{ margin: 0 }}>{typeLabel}</div>
                    {lt === 'ctv'
                      ? <span className="badge badge-amber" style={{ fontSize: '10px' }}>⚠ Testing Phase — Under Construction</span>
                      : <span className="badge badge-muted" style={{ fontSize: '10px' }}>checks {fileType}</span>}
                  </div>
                  {lt === 'ctv' && (
                    <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
                      <AlertTriangle size={13} />
                      <div>
                        <strong>CTV support is in a testing phase.</strong> Daily app-ads.txt checks are <strong>not run for CTV lists</strong> while testing is in progress — no line alerts or store-status alerts will be generated for these lists yet. You can create CTV lists and configure DSP lines now; automated daily checks will switch on once CTV support graduates from testing.
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {groupLists.map(list => {
                      const saved   = dspLines.find(d => d.list_id === list.id);
                      const draft   = dspDraft[list.id] || { dspName: saved?.dsp_name || '', adsTxtLine: saved?.ads_txt_line || '' };
                      const isDirty = draft.dspName !== (saved?.dsp_name || '') || draft.adsTxtLine !== (saved?.ads_txt_line || '');
                      return (
                        <div key={list.id} style={{ padding: '12px 14px', background: 'var(--bg-elevated)', border: `1px solid ${saved ? 'color-mix(in srgb, var(--green) 20%, transparent)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {list.name}
                              {saved
                                ? <span className="badge badge-green" style={{ fontSize: '9px' }}>✓ Configured</span>
                                : <span className="badge badge-muted" style={{ fontSize: '9px' }}>Not set</span>}
                            </div>
                            <span className="badge badge-muted">{list.count} entries</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: '8px', alignItems: 'center' }}>
                            <input className="input-field" placeholder="DSP Name (e.g. Criteo)"
                              value={draft.dspName}
                              onChange={e => setDspDraft(prev => ({ ...prev, [list.id]: { ...draft, dspName: e.target.value } }))} />
                            <input className="input-field"
                              style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }}
                              placeholder={`Primary ${fileType} line (e.g. criteo.com, 12345, RESELLER)`}
                              value={draft.adsTxtLine}
                              onChange={e => setDspDraft(prev => ({ ...prev, [list.id]: { ...draft, adsTxtLine: e.target.value } }))} />
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className={`btn btn-sm ${isDirty ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => saveDspLine(list.id)} disabled={dspSaving === list.id}>
                                {dspSaving === list.id ? '…' : saved ? 'Update' : 'Save'}
                              </button>
                              {saved && (
                                <button className="btn btn-danger btn-sm" onClick={() => deleteDspLine(list.id)}>
                                  <Trash2 size={11} />
                                </button>
                              )}
                            </div>
                          </div>
                          {saved && (
                            <div style={{ marginTop: '6px', fontSize: '10px', color: 'var(--text-muted)' }}>
                              Last updated {timeAgo(saved.updated_at)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── CHANGE HISTORY TAB ── */}
      {tab === 'history' && (
        <div>
          <div className="card" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <div className="card-title" style={{ margin: 0 }}><History size={13} /> Change History</div>
              <button className="btn btn-secondary btn-sm" onClick={exportHistoryCsv} disabled={histExporting || histLoading}>
                {histExporting
                  ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Exporting...</>
                  : <><Download size={12} /> Export CSV</>}
              </button>
            </div>

            {/* Filter bar */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>List</label>
                <select className="select-field" style={{ fontSize: '12px', minWidth: '180px' }} value={histListId} onChange={e => setHistListId(e.target.value)}>
                  <option value="all">All lists</option>
                  {lists.filter(l => histIncludeAuto || !l.id.startsWith('default-approved')).map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Action</label>
                <select className="select-field" style={{ fontSize: '12px' }} value={histAction} onChange={e => setHistAction(e.target.value)}>
                  <option value="all">All actions</option>
                  <optgroup label="Bundles">
                    <option value="added">Added</option>
                    <option value="bulk_added">Bulk Added</option>
                    <option value="removed">Removed</option>
                    <option value="bulk_removed">Bulk Removed</option>
                  </optgroup>
                  <optgroup label="Lists">
                    <option value="list_created">List Created</option>
                    <option value="list_deleted">List Deleted</option>
                    <option value="purpose_changed">Purpose Changed</option>
                    <option value="env_changed">Environment Changed</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From</label>
                <input type="date" className="input-field" style={{ fontSize: '12px' }} value={histFrom} onChange={e => setHistFrom(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>To</label>
                <input type="date" className="input-field" style={{ fontSize: '12px' }} value={histTo} onChange={e => setHistTo(e.target.value)} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer', paddingBottom: '6px' }}>
                <input type="checkbox" checked={histIncludeAuto} onChange={e => setHistIncludeAuto(e.target.checked)} style={{ cursor: 'pointer', accentColor: 'var(--cyan)' }} />
                Show auto-managed
              </label>
              {(histListId !== 'all' || histAction !== 'all' || histFrom || histTo || histIncludeAuto) && (
                <button className="btn btn-secondary btn-sm" style={{ paddingBottom: '6px' }}
                  onClick={() => { setHistListId('all'); setHistAction('all'); setHistFrom(''); setHistTo(''); setHistIncludeAuto(false); }}>
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="card">
            {histLoading ? (
              <div style={{ textAlign: 'center', padding: '30px' }}><span className="spinner" /></div>
            ) : histRows.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><History size={20} style={{ color: 'var(--text-muted)' }} /></div>
                <div className="empty-state-text">No change history</div>
                <div className="empty-state-sub">Add or remove bundles in a list to start building an audit trail</div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '130px' }}>When</th>
                      <th style={{ width: '180px' }}>List</th>
                      <th style={{ width: '110px' }}>Action</th>
                      <th style={{ width: '70px' }}>Count</th>
                      <th>Bundles</th>
                      <th style={{ width: '200px' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {histRows.map(e => (
                      <tr key={e.id}>
                        <td className="text-muted" title={e.timestamp ? new Date(e.timestamp).toLocaleString() : ''} style={{ whiteSpace: 'nowrap' }}>{timeAgo(e.timestamp)}</td>
                        <td title={`${e.listName || ''} (${e.listId || ''})`} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>
                          {e.listName || '-'}
                          {String(e.listId || '').startsWith('default-approved') && (
                            <span style={{ fontSize: '9px', color: 'var(--text-muted)', marginLeft: '5px', textTransform: 'uppercase' }}>auto</span>
                          )}
                        </td>
                        <td>{e.action && <ActionBadge action={e.action} />}</td>
                        <td>{e.bundleCount > 0 ? <span className="badge badge-muted">{e.bundleCount}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td className="mono" style={{ fontSize: '11px', color: 'var(--text-secondary)', wordBreak: 'break-all' }} title={(e.bundleIds || []).join(', ')}>
                          {(e.bundleIds || []).length === 0
                            ? <span style={{ color: 'var(--text-muted)' }}>—</span>
                            : <>{(e.bundleIds || []).slice(0, 6).join(', ')}{(e.bundleIds || []).length > 6 ? `  +${e.bundleIds.length - 6} more` : ''}</>}
                        </td>
                        <td title={e.notes || ''} style={{ fontSize: '11px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>{e.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {histTotal > 0 && (() => {
              const totalPages = Math.ceil(histTotal / histLimit);
              const start = (histPage - 1) * histLimit + 1;
              const end   = Math.min(histPage * histLimit, histTotal);
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Showing {start}-{end} of {histTotal}</span>
                    <select className="select-field" style={{ fontSize: '11px', padding: '2px 6px' }} value={histLimit}
                      onChange={e => { const sz = parseInt(e.target.value); setHistLimit(sz); loadHistory(1, sz); }}>
                      {[50, 100, 250, 500].map(n => <option key={n} value={n}>{n} / page</option>)}
                    </select>
                  </div>
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => loadHistory(1)} disabled={histPage === 1 || histLoading}>&laquo;</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => loadHistory(histPage - 1)} disabled={histPage === 1 || histLoading}><ChevronLeft size={12} /> Prev</button>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', padding: '0 8px' }}>Page {histPage} of {totalPages}</span>
                      <button className="btn btn-secondary btn-sm" onClick={() => loadHistory(histPage + 1)} disabled={histPage >= totalPages || histLoading}>Next <ChevronRight size={12} /></button>
                      <button className="btn btn-secondary btn-sm" onClick={() => loadHistory(totalPages)} disabled={histPage >= totalPages || histLoading}>&raquo;</button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {historyModal && <ChangeHistoryModal list={historyModal} onClose={() => setHistoryModal(null)} />}
      <NotesModal prompt={notesPrompt} onClose={() => setNotesPrompt(null)} />
    </div>
  );
}
